import { Controller } from "@hotwired/stimulus"

// Controller for managing contract form interactions
// Handles template loading and staff selection
export default class extends Controller {
  static targets = ["staffSelect", "typeSelect", "templateSelect", "contentEditor", "startDate", "endDate"]

  connect() {
    console.log("Contract form controller connected")
    // Initialize end date min value based on start date
    this.updateEndDateMin()
  }

  // Update end date minimum based on start date
  updateEndDateMin() {
    if (this.hasStartDateTarget && this.hasEndDateTarget) {
      const startDate = this.startDateTarget.value
      if (startDate) {
        this.setEndDateMinDate(startDate)
      }
    }
  }

  // Set minimum date on end date flatpickr
  setEndDateMinDate(minDate) {
    if (this.hasEndDateTarget) {
      const endDateFlatpickr = this.endDateTarget._flatpickr
      if (endDateFlatpickr) {
        endDateFlatpickr.set('minDate', minDate)
      } else {
        // Fallback for HTML5 date input
        this.endDateTarget.min = minDate
      }
    }
  }

  // Handle start date change - update end date minimum
  startDateChanged(event) {
    const startDate = event.target.value
    if (startDate) {
      this.setEndDateMinDate(startDate)

      // If end date is before start date, clear it
      if (this.hasEndDateTarget) {
        const endDateValue = this.endDateTarget.value
        if (endDateValue && endDateValue < startDate) {
          const endDateFlatpickr = this.endDateTarget._flatpickr
          if (endDateFlatpickr) {
            endDateFlatpickr.clear()
          } else {
            this.endDateTarget.value = ''
          }
        }
      }
    }
  }

  // Find the TinyMCE editor for the content textarea
  getEditor() {
    // Try to find editor by the content textarea
    if (this.hasContentEditorTarget) {
      const textareaId = this.contentEditorTarget.id
      if (textareaId && typeof tinymce !== 'undefined') {
        return tinymce.get(textareaId)
      }
    }

    // Fallback: find any editor in this form
    if (typeof tinymce !== 'undefined') {
      const textarea = this.element.querySelector('textarea.tinymce-editor')
      if (textarea && textarea.id) {
        return tinymce.get(textarea.id)
      }
      // Last fallback: use active editor
      return tinymce.activeEditor
    }

    return null
  }

  // Load template content into the editor
  async loadTemplate(event) {
    event.preventDefault()

    const templateSelect = this.templateSelectTarget
    const templateId = templateSelect.value

    // If no template selected
    if (!templateId) {
      // Show alert only if triggered by button click
      if (event.type === 'click') {
        alert("Veuillez sélectionner un modèle")
      } else {
        // Clear editor when selecting empty option
        const editor = this.getEditor()
        if (editor) {
          editor.setContent('')
        } else {
          const textarea = this.element.querySelector('textarea[name*="content"]')
          if (textarea) {
            textarea.value = ''
          }
        }
      }
      return
    }

    // Get current locale from URL
    const locale = window.location.pathname.split('/')[1] || 'fr'

    // Get selected staff if available
    let staffId = ''
    if (this.hasStaffSelectTarget) {
      staffId = this.staffSelectTarget.value
    }

    try {
      let url = `/${locale}/schools/rh/contracts/staff_contracts/load_template?template_id=${templateId}`
      if (staffId) {
        url += `&staff_id=${staffId}`
      }

      console.log("Loading template from:", url)

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Server error:', errorText)
        throw new Error('Failed to load template')
      }

      const data = await response.json()
      console.log("Template data received:", data)

      // Insert content into TinyMCE editor
      const editor = this.getEditor()
      if (editor) {
        editor.setContent(data.content || '')
        console.log("Template content set in TinyMCE editor")
      } else {
        // Fallback to textarea
        const textarea = this.element.querySelector('textarea[name*="content"]')
        if (textarea) {
          textarea.value = data.content || ''
          console.log("Template content set in textarea (fallback)")
        } else {
          console.warn("No editor or textarea found to set content")
        }
      }

      console.log("Template loaded successfully")
    } catch (error) {
      console.error('Error loading template:', error)
      alert("Erreur lors du chargement du modèle")
    }
  }

  // Handle staff selection change
  staffChanged(event) {
    console.log("Staff changed:", event.target.value)
    // Could be used to pre-fill contract title or other fields
  }

  // Handle contract type selection change
  typeChanged(event) {
    console.log("Contract type changed:", event.target.value)
    // Could be used to filter templates by type
  }
}
